import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  BondFormat,
  BondFormatApiItem,
  DeleteBondFormatResult,
  UpsertBondFormatRequest,
} from './bond-format.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class BondFormatService {
  private readonly endpoint = apiUrl('bondformat');

  constructor(private readonly http: HttpClient) {}

  getBondFormats(): Observable<BondFormat[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeBondFormat(item))),
    );
  }

  createBondFormat(payload: UpsertBondFormatRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateBondFormat(id: number, payload: UpsertBondFormatRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteBondFormat(id: number): Observable<DeleteBondFormatResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteBondFormatResult => ({ kind: 'success' })),
      catchError(() => of<DeleteBondFormatResult>({
        kind: 'error',
        message: 'Unable to delete bond format at this time. Please try again.',
      })),
    );
  }

  private normalizeBondFormat(item: BondFormatApiItem): BondFormat {
    return {
      id: this.toNumber(item.id),
      format: this.toText(item.format),
      description: this.toText(item.description),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: BondFormatApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): BondFormatApiItem[] {
    if (Array.isArray(response)) {
      return response as BondFormatApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as BondFormatApiItem[];
      }

      if (Array.isArray(items)) {
        return items as BondFormatApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
